/**
 * Put a `position: fixed` overlay where `position: fixed` actually means the viewport.
 *
 * THE TRAP. Pull-to-refresh translates the page as the finger comes down:
 *
 *   .shell > .chrome, .shell > main { transform: translateY(var(--ptr-shift, 0px)); }
 *
 * A transform — even `translateY(0px)`, even when nobody is pulling — makes that element the
 * containing block for every `position: fixed` DESCENDANT. So a sheet rendered inside `main` with
 * `position: fixed; bottom: 0` does not sit at the bottom of the screen. It sits at the bottom of
 * `main`, which on the keeper declaration screen is 2,837px tall.
 *
 * That is not a cosmetic bug. On 26 August Tim reported two things that turned out to be this
 * one: the withdraw confirmation "was not visible when I tapped it — I had to scroll down to find
 * it and it was covered by the buttons", and, worse, a slate that submitted itself. The submit
 * sheet was landing at y=2626 in an 844px viewport with its "Submit slate" button at y=2887 —
 * so pressing Submit appeared to do nothing at all, and the confirmation that decides an
 * irreversible league action was sitting nearly two thousand pixels down the page where it could
 * be pressed by somebody who had no idea it was there.
 *
 * `CommitBar` already worked around this for itself. This is that fix, extracted, so the next
 * overlay does not have to rediscover it: any overlay whose CSS says `fixed` renders through here.
 *
 * `tests/dockedOverlays.test.ts` holds the list and fails on a new one that does not.
 */

import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * Render `node` into `document.body` rather than where it appears in the tree.
 *
 * Falls back to rendering in place where there is no document, which is only server rendering and
 * static analysis — neither of which has a transform to be caught by.
 */
export function dock(node: ReactNode): ReactNode {
  return typeof document === 'undefined' ? node : createPortal(node, document.body);
}

/**
 * Dock only where the overlay is ACTUALLY fixed.
 *
 * Some bars are `position: fixed` at one width and an ordinary block at another. The live strips
 * are both: under 760px they are pinned above the tab bar, and above that they sit in the flow
 * inside `.chrome`, which is `position: sticky` and carries them down the page as it should.
 *
 * Docking those unconditionally is a fix that breaks the other half. A static element portalled
 * into `document.body` renders after `#root` — that is, at the very bottom of the document — so
 * the desktop draft strip went from sitting above the top nav to sitting below the last card on
 * the page. Found on 26 August by measuring the committee strip's `top` at 1845px in a 900px
 * viewport, which is the same number the draft strip would have given all day.
 *
 * So: portal when it is fixed, leave it where it was written when it is not.
 */
export function dockWhen(fixed: boolean, node: ReactNode): ReactNode {
  return fixed ? dock(node) : node;
}
